import React, { useState, useEffect } from "react";
import "../Styles/Header.css";
import { FaSearch } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";

// 👇 Import both versions of your logo
import logoDark from "../Assets/bigLogo.png";
import logoLight from "../Assets/newBigWHiteLogo-removebg-preview.png";
import { motion, useScroll, useTransform } from "framer-motion";


const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState("nav"); // 'nav' | 'info'
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);

  const location = useLocation();
  const isHome = location.pathname === "/";

  const { scrollY } = useScroll();
  const headerNavOpacity = useTransform(scrollY, [0, window.innerHeight * 0.5], [0, 1]);


  // Scroll detection (only matters on home)
  useEffect(() => {
    if (!isHome) {
      setScrolled(true); // Always show nav if not home
      return;
    }
    const handleScroll = () =>
      setScrolled(window.scrollY > window.innerHeight * 0.7);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHome]);

  // Detect resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 991);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleDrawer = (type) => {
    setDrawerType(type);
    setDrawerOpen((prev) => !prev);
  };

  const closeDrawer = () => setDrawerOpen(false);

  // Scroll to top whenever route changes
  useEffect(() => {
    window.scrollTo(0, 0);

    // 👇 Reset scrolled when coming back to home
    if (isHome) {
      setScrolled(false);
    } else {
      setScrolled(true); // non-home pages always show header nav
    }
  }, [location, isHome]);

  // 👇 Choose logo based on scroll and page
  const logoToShow =
    isHome ? logoLight : logoDark;  

  return (
    <header
      className={`main-header ${scrolled ? "scrolled" : ""} ${
        !isHome ? "header-white" : ""
      }`}
    >
      <div className="header-container">
        {/* LEFT: Logo */}
        <div className="logo">
          <Link to="/">
            <img src={logoToShow} alt="T-Rides Logo" className={`logo-img ${logoToShow === logoLight ? "logo-light" : "logo-dark"}`}/>
          </Link>
        </div>

        {/* CENTER: Desktop Nav */}
        {!isMobile && (
          <motion.nav
            className="header-nav"
            style={{
              opacity: isHome ? headerNavOpacity : 1, // always visible on non-home
              pointerEvents: isHome && scrollY.get() < window.innerHeight * 0.3 ? "none" : "auto",
            }}
          >
            <Link to="/">HOME</Link>
            <Link to="/vehicle-fleet">VEHICLE FLEET</Link>
            <Link to="/contact-us">CONTACT</Link>
            <Link to="/about-us">ABOUT</Link>
          </motion.nav>
        )}


        {/* RIGHT: Contact / Search + Info Hamburger */}
        <div className="header-actions">
          {!isMobile && !scrolled && isHome && (
            <div className="contact-info">
              <span>📞 (234) 703 0792 420</span>
              <span>🏢 41 Gade Street Gwarinpa Abuja, FCT.</span>
            </div>
          )}

          {!isMobile && scrolled && (
            <>
              <button className="search-btn" aria-label="Search">
                <FaSearch className="search-icon" />
              </button>
              <button
                className={`hamburger ${
                  drawerOpen && drawerType === "info" ? "open" : ""
                }`}
                onClick={() => toggleDrawer("info")}
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </>
          )}

          {/* Mobile Hamburger */}
          {isMobile && (
            <button
              className={`hamburger ${
                drawerOpen && drawerType === "nav" ? "open" : ""
              }`}
              onClick={() => toggleDrawer("nav")}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          )}
        </div>

        {/* MOBILE NAV DRAWER */}
        {isMobile && (
          <div
            className={`mobile-nav ${
              drawerOpen && drawerType === "nav" ? "open" : ""
            }`}
          >
            <Link to="/" onClick={closeDrawer}>
              Home
            </Link>
            <Link to="/vehicle-fleet" onClick={closeDrawer}>
              VEHICLE FLEET
            </Link>
            <Link to="/contact-us" onClick={closeDrawer}>
              CONTACT
            </Link>
            <Link to="/about-us" onClick={closeDrawer}>
              ABOUT
            </Link>
          </div>
        )}

        {/* DESKTOP INFO DRAWER */}
        {!isMobile && scrolled && (
          <aside
            className={`info-drawer ${
              drawerOpen && drawerType === "info" ? "open" : ""
            }`}
          >
            <div className="info-body">
              <p>
                At T-Rides, we believe every journey should be more than just a
                transfer from one point to another — it should be an experience of
                comfort, reliability, and elegance. As a premier transportation
                service, we specialize in delivering seamless rides tailored to
                executives, professionals, families, and organizations who value
                both time and quality.
              </p>
              <p>
                Our mission is simple: to redefine travel across Abuja and beyond by
                setting a new benchmark for luxury, reliability, and affordability
                in the industry. With every ride, we strive to build lasting
                relationships with our clients by exceeding expectations and
                offering a service that is both dependable and memorable.
              </p>  

              {/* <p>
                When you choose T-Rides, you’re not just booking a vehicle — you’re
                choosing a brand that values professionalism, prioritizes your
                comfort, and ensures that every journey feels effortless. Your ride
                is more than transportation; it’s an experience designed for you.
              </p> */}

              <div className="hours">
                <strong>Mon - Sat:</strong> <span>9:00 - 23:00</span>
              </div>
              <div className="hours">
                <strong>Sun:</strong> <span>Closed</span>
              </div>
              <div className="phone">
                <strong>Call:</strong> <span>(234) 703 0792 420</span>
              </div>
            </div>
          </aside>
        )}

        {/* Overlay for drawers */}
        {drawerOpen && <div className="drawer-overlay" onClick={closeDrawer} />}
      </div>
    </header>
  );
};

export default Header;
